/**
 * DrawdownRemindersTab — Phase G
 * Lets the user pick an APPROVED drawdown_investor template and bulk-send
 * reminder emails to investors who still have an outstanding amount.
 *
 * Two sub-filter tabs:
 *   • Pending           — investor never paid (zero receipts)
 *   • Partially Pending — investor paid some but still owes money
 *
 * Same composer options as Drawdown Notice emails (auto-attach reviewed
 * notice PDF, BCC self, date format, number format, preview pane).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Bell, Send, FileText, AlertCircle, CheckCircle, Loader2, X, StopCircle,
  Paperclip, RefreshCw, Eye, Mail
} from 'lucide-react';
import { Link } from 'react-router-dom';
import EmailPreviewModal from '../../components/email/EmailPreviewModal';
import TeamMemberPicker from '../../components/email/TeamMemberPicker';
import RecipientSearchBar, { filterByQuery } from '../../components/email/RecipientSearchBar';
import useWhatsAppSend from '../../hooks/useWhatsAppSend';
import WhatsAppSendOptions from '../../components/whatsapp/WhatsAppSendOptions';
import WhatsAppSendResultModal from '../../components/whatsapp/WhatsAppSendResultModal';
import WhatsAppSendHistory from '../../components/whatsapp/WhatsAppSendHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:    { cls: 'bg-gray-100 text-gray-700',   label: 'Pending' },
  running:    { cls: 'bg-blue-100 text-blue-700',   label: 'Sending...' },
  completed:  { cls: 'bg-green-100 text-green-800', label: 'Completed' },
  aborted:    { cls: 'bg-yellow-100 text-yellow-800', label: 'Aborted' },
  failed:     { cls: 'bg-red-100 text-red-800',     label: 'Failed' },
};

const REC_STATUS_META = {
  pending: { cls: 'bg-gray-100 text-gray-600',   label: 'Pending' },
  sent:    { cls: 'bg-green-100 text-green-700', label: 'Sent' },
  failed:  { cls: 'bg-red-100 text-red-700',     label: 'Failed' },
  skipped: { cls: 'bg-yellow-100 text-yellow-700', label: 'Skipped' },
  aborted: { cls: 'bg-yellow-100 text-yellow-700', label: 'Aborted' },
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
};

const DrawdownRemindersTab = ({ drawdownId, currentUser }) => {
  const [filter, setFilter] = useState('all'); // all | pending | partially_pending
  const [counts, setCounts] = useState({ all: 0, pending: 0, partially_pending: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [includeNotice, setIncludeNotice] = useState(true);
  const [attachProtected, setAttachProtected] = useState(true);
  const [bccSelf, setBccSelf] = useState(false);
  const [dateFormat, setDateFormat] = useState('dd mmmm yyyy');
  const [numberFormat, setNumberFormat] = useState('western');
  const [selectedIds, setSelectedIds] = useState([]);
  const [consolidateByAccount, setConsolidateByAccount] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [ccUserIds, setCcUserIds] = useState([]);
  const [bccUserIds, setBccUserIds] = useState([]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  const [history, setHistory] = useState([]);

  // -------- WhatsApp parallel send --------
  const wa = useWhatsAppSend(drawdownId, 'reminders');

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line
  }, [drawdownId, filter]);

  useEffect(() => {
    loadTemplates();
    loadHistory();
    // eslint-disable-next-line
  }, [drawdownId]);

  // Poll active job
  useEffect(() => {
    if (!activeJob) return;
    if (['completed', 'aborted', 'failed'].includes(activeJob.status)) return;
    const tick = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (['completed', 'aborted', 'failed'].includes(data.status)) {
          loadHistory();
          loadRows();
        }
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [activeJob?.id, activeJob?.status]);

  const loadRows = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/reminders?filter=${filter}`);
      setRows(data.rows || []);
      setCounts(data.counts || { all: 0, pending: 0, partially_pending: 0 });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/email-templates?scope=approved&section=reminders`);
      setTemplates((data || []).filter(t => ['drawdown_investor', 'general'].includes(t.context_type)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load approved templates');
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/history`);
      setHistory((data || []).filter(j => j.kind === 'drawdown_reminder'));
    } catch { /* ignore */ }
  };

  const toggleRow = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const visibleRows = useMemo(
    () => filterByQuery(rows, recipientSearch, ['account_name', 'class_name', 'folio_no', 'pan']),
    [rows, recipientSearch]
  );
  const toggleAll = () => {
    const ids = visibleRows.map(r => r.drawdown_investor_id);
    const allOn = ids.length > 0 && ids.every(id => selectedIds.includes(id));
    if (allOn) setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    else setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const [sendEmail, setSendEmail] = useState(true);

  const handleSend = async () => {
    setError(''); setSuccess('');
    if (!selectedTemplate) { setError('Pick an approved template first.'); return; }
    if (selectedIds.length === 0) { setError('Select at least one investor.'); return; }
    if (!sendEmail && !wa.canSend) {
      setError('Nothing to send — enable the email checkbox or the "Also send WhatsApp" toggle.');
      return;
    }
    try {
      let emailQueued = 0;
      let skippedFullyPaid = 0;
      if (sendEmail) {
        const { data } = await axios.post(`${API}/drawdowns/${drawdownId}/reminders/send`, {
          template_id: selectedTemplate,
          drawdown_investor_ids: selectedIds,
          include_drawdown_notice: includeNotice,
          attach_protected: attachProtected,
          bcc_self: bccSelf,
          date_format: dateFormat,
          number_format: numberFormat,
          cc_user_ids: ccUserIds,
          bcc_user_ids: bccUserIds,
          consolidate_by_account: consolidateByAccount,
        });
        const { data: job } = await axios.get(`${API}/drawdowns/${drawdownId}/emails/jobs/${data.job_id}`);
        setActiveJob(job);
        emailQueued = data.total;
        skippedFullyPaid = (data.skipped_fully_paid || []).length;
      }

      // Optional parallel WhatsApp reminder send — independent template, same recipients.
      if (wa.canSend) {
        const res = await wa.triggerSend({
          drawdownInvestorIds: selectedIds,
          preferProtected: attachProtected,
          dateFormat,
          numberFormat,
        });
        if (!res.ok) {
          setError(sendEmail
            ? `Reminders queued OK, but WhatsApp send failed: ${res.error}`
            : `WhatsApp reminder failed: ${res.error}`);
          return;
        }
      }
      setSelectedIds([]);
      if (sendEmail && wa.canSend) {
        setSuccess(`Queued ${emailQueued} reminder email(s) + a parallel WhatsApp send.` + (skippedFullyPaid ? ` Skipped ${skippedFullyPaid} fully-paid investor(s).` : ''));
      } else if (sendEmail) {
        setSuccess(`Queued ${emailQueued} reminder email(s).` + (skippedFullyPaid ? ` Skipped ${skippedFullyPaid} fully-paid investor(s).` : ''));
      } else {
        setSuccess('WhatsApp reminder queued — see the progress modal for live status.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start send');
    }
  };

  const handleAbort = async () => {
    if (!activeJob) return;
    if (!window.confirm('Abort sending? Already-sent emails cannot be unsent.')) return;
    try {
      await axios.post(`${API}/drawdowns/${drawdownId}/emails/jobs/${activeJob.id}/abort`);
      setSuccess('Abort requested. Stopping after the current recipient...');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to abort'); }
  };

  const senderEnabled = !!currentUser?.email_sending_enabled;
  const sendingActive = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');
  const progressPct = activeJob && activeJob.total > 0
    ? Math.round(((activeJob.sent || 0) + (activeJob.failed || 0) + (activeJob.skipped || 0)) / activeJob.total * 100)
    : 0;
  const allSelectedOnPage = rows.length > 0 && rows.every(r => selectedIds.includes(r.drawdown_investor_id));

  return (
    <div className="space-y-6" data-testid="dd-reminders-tab">
      {!senderEnabled && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-testid="dd-reminders-disabled-notice">
          <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold">You can't send emails from your account yet.</p>
            <p className="mt-1">Ask an Admin to enable "Allow this user to send emails" for you in <strong>User Management</strong>, then save your SMTP credentials in <Link className="underline text-blue-700" to="/email-config">Email Configuration</Link>.</p>
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-900 flex items-center mb-4">
          <Bell size={16} className="mr-2 text-amber-600" /> Send Drawdown Reminder Emails
        </h3>

        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800 flex items-start" data-testid="dd-reminders-error">
            <AlertCircle size={14} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center" data-testid="dd-reminders-success">
            <CheckCircle size={14} className="text-green-600 mr-2" /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Approved Template *</label>
            <select
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-reminders-template-select"
            >
              <option value="">— Pick a template —</option>
              {templates.length === 0 && <option disabled>(no approved templates yet)</option>}
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} · {t.context_type === 'drawdown_investor' ? 'Drawdown' : 'General'}</option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Need to <Link className="text-blue-600 underline" to="/email-templates/new">create one</Link> first and have an Admin approve it.
              </p>
            )}
          </div>
          <div className="space-y-2 pt-1">
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" checked={includeNotice} onChange={e => setIncludeNotice(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" data-testid="dd-reminders-include-notice" />
              <span className="ml-2 text-sm text-gray-700"><Paperclip size={12} className="inline mr-1" />Auto-attach the reviewed drawdown notice (PDF) for each investor</span>
            </label>
            {includeNotice && (
              <div className="ml-6 mt-1 flex flex-wrap items-center gap-4 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-testid="dd-reminders-protect-choice">
                <span className="font-medium text-gray-600">Attach as:</span>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio" name="dd-reminders-attach-protected"
                    checked={attachProtected === true}
                    onChange={() => setAttachProtected(true)}
                    className="h-3.5 w-3.5 text-blue-600"
                    data-testid="dd-reminders-attach-protected-yes"
                  />
                  <span className="ml-1.5">Password-protected PDF (recommended)</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio" name="dd-reminders-attach-protected"
                    checked={attachProtected === false}
                    onChange={() => setAttachProtected(false)}
                    className="h-3.5 w-3.5 text-blue-600"
                    data-testid="dd-reminders-attach-protected-no"
                  />
                  <span className="ml-1.5">Unprotected PDF (no password)</span>
                </label>
                <span className="text-[11px] text-gray-500 ml-1">If a protected copy doesn&apos;t exist, the unprotected version is sent regardless.</span>
              </div>
            )}
            <label className="flex items-start cursor-pointer" data-testid="dd-reminders-send-email-toggle">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={e => setSendEmail(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded mt-0.5"
              />
              <span className="ml-2 text-sm text-gray-700">
                <Mail size={12} className="inline mr-1 text-blue-600" />
                Send email reminder
                <span className="text-gray-500"> — uncheck if you only want to re-send the WhatsApp reminder (e.g. retrying after a phone-number fix).</span>
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" checked={bccSelf} onChange={e => setBccSelf(e.target.checked)} disabled={!sendEmail} className={`h-4 w-4 text-blue-600 rounded ${!sendEmail ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="dd-reminders-bcc-self" />
              <span className={`ml-2 text-sm ${sendEmail ? 'text-gray-700' : 'text-gray-400'}`}>BCC me on every reminder</span>
            </label>
            {rows.some(r => r.class_name) && (
              <label className="flex items-start cursor-pointer">
                <input type="checkbox" checked={consolidateByAccount} onChange={e => setConsolidateByAccount(e.target.checked)} className="h-4 w-4 text-blue-600 rounded mt-0.5" data-testid="dd-reminders-consolidate-account" />
                <span className="ml-2 text-sm text-gray-700">Send a single reminder per account<span className="text-gray-500"> — combines every pending class into one email and attaches all class PDFs.</span></span>
              </label>
            )}
            {/* WhatsApp parallel send — picks a published drawdown WhatsApp
                template independently of the email template. Honours per-contact
                opt-out and reuses the same Drawdown WhatsApp send endpoint. */}
            <WhatsAppSendOptions
              wa={wa}
              label="fires a parallel WhatsApp reminder over the same selected recipients."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date Format <span className="text-gray-400 ml-1">(date merge fields & receipt date column)</span></label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-reminders-date-format-select">
              <option value="dd mmmm yyyy">25 January 2026</option>
              <option value="dd-mm-yyyy">25-01-2026</option>
              <option value="mm-dd-yyyy">01-25-2026</option>
              <option value="dd/mm/yyyy">25/01/2026</option>
              <option value="mm/dd/yyyy">01/25/2026</option>
              <option value="yyyy-mm-dd">2026-01-25</option>
              <option value="mmmm dd, yyyy">January 25, 2026</option>
              <option value="dd-mmmm-yyyy">25-January-2026</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Number Format <span className="text-gray-400 ml-1">(amount columns & numeric merge fields)</span></label>
            <select value={numberFormat} onChange={e => setNumberFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="dd-reminders-number-format-select">
              <option value="western">Western (1,000,000)</option>
              <option value="indian">Indian (10,00,000)</option>
            </select>
          </div>
        </div>

        {/* Team Member Cc / Bcc */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <TeamMemberPicker
            label="CC team members"
            value={ccUserIds}
            onChange={setCcUserIds}
            placeholder="Add teammates to Cc on every reminder…"
            testid="dd-reminders-cc-team"
          />
          <TeamMemberPicker
            label="BCC team members"
            value={bccUserIds}
            onChange={setBccUserIds}
            placeholder="Add teammates to Bcc on every reminder…"
            testid="dd-reminders-bcc-team"
          />
        </div>

        {/* Sub-filter tabs */}
        <div className="flex flex-wrap items-center justify-between mb-2">
          <div className="flex items-center gap-1 border-b border-gray-200" role="tablist">
            {[
              { id: 'all', label: 'All', count: counts.all },
              { id: 'pending', label: 'Pending', count: counts.pending },
              { id: 'partially_pending', label: 'Partially Pending', count: counts.partially_pending },
            ].map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => { setFilter(t.id); setSelectedIds([]); }}
                role="tab"
                aria-selected={filter === t.id}
                className={`px-3 py-1.5 text-sm border-b-2 -mb-px transition-colors ${
                  filter === t.id ? 'border-amber-600 text-amber-700 font-semibold' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`dd-reminders-filter-${t.id}`}
              >
                {t.label} <span className="ml-1 text-xs text-gray-400">({t.count})</span>
              </button>
            ))}
          </div>
          <button onClick={loadRows} className="text-xs text-gray-500 hover:text-gray-800 flex items-center" title="Refresh" data-testid="dd-reminders-refresh">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </button>
        </div>

        {/* Investor list */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-semibold text-gray-700">Recipients ({selectedIds.length} of {rows.length} on this page)</span>
            <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-[400px]">
              <RecipientSearchBar value={recipientSearch} onChange={setRecipientSearch} placeholder="Search investor, class, folio, PAN…" testid="dd-reminders-recipient-search" className="flex-1" />
              <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline disabled:opacity-50 whitespace-nowrap" data-testid="dd-reminders-select-all" disabled={visibleRows.length === 0}>
                {visibleRows.length > 0 && visibleRows.every(r => selectedIds.includes(r.drawdown_investor_id)) ? 'Clear visible' : 'Select all visible'}
              </button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <p className="p-4 text-sm text-gray-500 italic text-center"><Loader2 size={14} className="inline animate-spin mr-1" />Loading...</p>
            ) : rows.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 italic text-center">
                {filter === 'all'
                  ? 'No outstanding investors — all payments have been received for this drawdown.'
                  : filter === 'pending'
                  ? 'No fully-pending investors — every selected one has at least started paying.'
                  : 'No partially-pending investors right now.'}
              </p>
            ) : visibleRows.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 italic text-center" data-testid="dd-reminders-recipients-empty">No investors match "{recipientSearch}".</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {visibleRows.map(r => (
                    <tr key={r.drawdown_investor_id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2 w-8">
                        <input type="checkbox"
                          checked={selectedIds.includes(r.drawdown_investor_id)}
                          onChange={() => toggleRow(r.drawdown_investor_id)}
                          data-testid={`dd-reminders-pick-${r.drawdown_investor_id}`}
                          className="h-4 w-4 text-blue-600 rounded" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900 flex items-center">
                          {r.account_name}
                          <span className={`ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold ${
                            r.category === 'pending' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>
                            {r.category === 'pending' ? 'Pending' : 'Partial'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {r.class_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded mr-1">{r.class_name}</span>}
                          {r.ppm_no && <span>PPM: {r.ppm_no} · </span>}
                          {r.folio_no && <span>Folio: {r.folio_no}</span>}
                          {r.last_reminder_status && (
                            <span className="ml-2">
                              · Last reminder: <span className={`px-1.5 py-0.5 rounded text-[10px] ${(REC_STATUS_META[r.last_reminder_status] || REC_STATUS_META.pending).cls}`}>
                                {(REC_STATUS_META[r.last_reminder_status] || REC_STATUS_META.pending).label}
                              </span>
                              {r.last_reminder_sent_at && <span className="ml-1 text-gray-400">{fmtDate(r.last_reminder_sent_at)}</span>}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                        <div className="text-gray-700">Outstanding: <span className="font-mono font-semibold text-amber-700">{(r.outstanding_amount || 0).toLocaleString()}</span></div>
                        <div className="text-gray-400">of {(r.drawdown_amount || 0).toLocaleString()}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={!selectedTemplate || selectedIds.length === 0}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dd-reminders-preview-btn"
            title="Preview the reminder for one selected investor"
          >
            <Eye size={14} className="mr-2" /> Preview
          </button>
          <button
            onClick={handleSend}
            disabled={!senderEnabled || sendingActive || templates.length === 0 || selectedIds.length === 0 || !selectedTemplate}
            className="px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dd-reminders-send-btn"
          >
            <Send size={14} className="mr-2" /> Send Reminder to {selectedIds.length || '...'} Investor{selectedIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        drawdownId={drawdownId}
        templateId={selectedTemplate}
        kind="drawdown_reminder"
        recipients={rows
          .filter(r => selectedIds.includes(r.drawdown_investor_id))
          .map(r => ({ id: r.drawdown_investor_id, account_name: r.account_name, class_name: r.class_name, folio_no: r.folio_no }))}
        includeDrawdownNotice={includeNotice}
        bccSelf={bccSelf}
        dateFormat={dateFormat}
        numberFormat={numberFormat}
        ccUserIds={ccUserIds}
        bccUserIds={bccUserIds}
        extraPayload={{ attach_protected: attachProtected }}
      />

      {/* WhatsApp send result modal */}
      {wa.resultOpen && wa.activeJob && (
        <WhatsAppSendResultModal
          job={wa.activeJob}
          onClose={() => wa.setResultOpen(false)}
        />
      )}

      <WhatsAppSendHistory
        drawdownId={drawdownId}
        refreshKey={(wa.activeJob && ['completed','failed','aborted'].includes(wa.activeJob.status)) ? wa.activeJob.id : 0}
      />

      {/* Active job */}
      {activeJob && (
        <div className="bg-white border border-amber-300 rounded-lg p-5" data-testid="dd-reminders-active-job">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <Loader2 size={14} className={`mr-2 ${sendingActive ? 'animate-spin text-amber-600' : 'text-gray-400'}`} />
              {sendingActive ? 'Sending reminders...' : 'Last send result'}
            </h3>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-full font-medium ${(STATUS_META[activeJob.status] || STATUS_META.pending).cls}`} data-testid="dd-reminders-job-status">
                {(STATUS_META[activeJob.status] || STATUS_META.pending).label}
              </span>
              {sendingActive && (
                <button onClick={handleAbort} className="flex items-center bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100" data-testid="dd-reminders-abort-btn">
                  <StopCircle size={12} className="mr-1" /> Abort
                </button>
              )}
              <button onClick={() => setActiveJob(null)} className="text-gray-400 hover:text-gray-700"><X size={12} /></button>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2"><div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} /></div>
          <p className="text-xs text-gray-600 text-center">{activeJob.sent || 0} sent · {activeJob.failed || 0} failed · {activeJob.skipped || 0} skipped · of {activeJob.total} total</p>
          {!sendingActive && (activeJob.recipients || []).length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto border border-gray-100 rounded">
              <table className="w-full text-xs">
                <tbody>
                  {(activeJob.recipients || []).map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0">
                      <td className="px-2 py-1 text-gray-700">{r.account_name}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded ${(REC_STATUS_META[r.status] || REC_STATUS_META.pending).cls}`}>
                          {(REC_STATUS_META[r.status] || REC_STATUS_META.pending).label}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-gray-500">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="dd-reminders-history">
          <h3 className="text-sm font-bold text-gray-900 flex items-center mb-3">
            <FileText size={14} className="mr-2 text-gray-500" /> Reminder Send History
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Sent</th>
                  <th className="px-3 py-2 text-right">Failed</th>
                  <th className="px-3 py-2 text-right">Skipped</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map(j => (
                  <tr key={j.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-700">{fmtDate(j.created_at)}</td>
                    <td className="px-3 py-2 text-gray-700">{j.template_name || '—'}</td>
                    <td className="px-3 py-2 text-right">{j.total}</td>
                    <td className="px-3 py-2 text-right text-green-700 font-medium">{j.sent}</td>
                    <td className="px-3 py-2 text-right text-red-700 font-medium">{j.failed}</td>
                    <td className="px-3 py-2 text-right text-yellow-700 font-medium">{j.skipped}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${(STATUS_META[j.status] || STATUS_META.pending).cls}`}>{(STATUS_META[j.status] || STATUS_META.pending).label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawdownRemindersTab;
